import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { api } from '@api/client';
import { useRecentActions } from '@components/features/Overview/QuickLinks/hook';
import { paymentsParams } from '@components/features/Payments/params';
import { useStrongParams } from '@utils/params';

import {
  Acquirer,
  AcquirerConfiguration,
  AcquirerNetwork,
  ListAcquirersResponse,
} from './types';

export const queryKeys = {
  paginatedAcquirers: (
    appUuid: string,
    params: UsePaginatedAcquirersParams
  ) => [appUuid, 'acquirers', 'page', params],
  acquirer: (appUuid: string, acquirerUuid: string) => [
    appUuid,
    'acquirers',
    acquirerUuid,
  ],
};

export const queryFilters = {
  paginatedAcquirers: (appUuid: string) => ({
    queryKey: [appUuid, 'acquirers', 'page'],
    exact: false,
  }),
  acquirer: (appUuid: string, acquirerUuid: string) => ({
    queryKey: queryKeys.acquirer(appUuid, acquirerUuid),
    exact: true,
  }),
};

export interface UsePaginatedAcquirersParams {
  page?: number;
  pageSize?: number;
}

export function usePaginatedAcquirers(
  params?: UsePaginatedAcquirersParams,
  options?: UseQueryOptions<
    ListAcquirersResponse,
    unknown,
    ListAcquirersResponse,
    ReturnType<typeof queryKeys.paginatedAcquirers>
  >
) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;

  return useQuery({
    queryKey: queryKeys.paginatedAcquirers(appUuid, { page, pageSize }),
    async queryFn() {
      const response = await api(appUuid).get<ListAcquirersResponse>(
        'payments/acquirers',
        {
          searchParams: {
            page: page - 1,
            pageSize,
          },
        }
      );
      const data = await response.json();
      data.data.forEach((acquirer) => {
        queryClient.setQueryData(
          queryKeys.acquirer(appUuid, acquirer.id),
          acquirer
        );
      });
      return data;
    },
    keepPreviousData: true,
    ...options,
  });
}

async function getAcquirer(appUuid: string, acquirerUuid: string) {
  const response = await api(appUuid).get<Acquirer>(
    `payments/acquirers/${acquirerUuid}`
  );
  return response.json();
}

export function useAcquirer(
  acquirerUuid: string,
  options?: UseQueryOptions<Acquirer, unknown, Acquirer, string[]>
) {
  const { appUuid } = useStrongParams(paymentsParams);
  return useQuery({
    queryKey: queryKeys.acquirer(appUuid, acquirerUuid),
    queryFn: () => getAcquirer(appUuid, acquirerUuid),
    ...options,
  });
}

export function useCreateAcquirer() {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (data: Pick<Acquirer, 'name' | 'description'>) => {
      const response = await api(appUuid).post<Acquirer>('payments/acquirers', {
        json: { ...data, configurations: [] },
      });
      return response.json();
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.acquirer(appUuid, data.id), data);
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}

export function useUpdateAcquirer(acquirerUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: (
      data: Partial<Pick<Acquirer, 'name' | 'description' | 'default'>>
    ) => patchAcquirer(appUuid, acquirerUuid, data),
    onSuccess(data) {
      queryClient.setQueryData<Acquirer>(
        queryKeys.acquirer(appUuid, acquirerUuid),
        (existing) =>
          existing
            ? { ...existing, ...data, configurations: existing.configurations }
            : data
      );
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}

export function useDeleteAcquirer(acquirerUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  const recents = useRecentActions();
  return useMutation({
    mutationFn: async () => {
      await api(appUuid).delete(`payments/acquirers/${acquirerUuid}`);
    },
    onSuccess() {
      for (const [
        key,
        page,
      ] of queryClient.getQueriesData<ListAcquirersResponse>(
        queryFilters.paginatedAcquirers(appUuid)
      )) {
        if (!page) continue;
        queryClient.setQueryData(key, {
          ...page,
          data: page.data.filter((a) => a.id !== acquirerUuid),
          total: page.total - 1,
        });
      }
      recents.remove({ type: 'acquirer', id: acquirerUuid });
      queryClient.removeQueries(queryFilters.acquirer(appUuid, acquirerUuid));
    },
    onSettled() {
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}

async function patchAcquirer(
  appUuid: string,
  acquirerUuid: string,
  data: Partial<Acquirer>
) {
  const response = await api(appUuid).patch<Acquirer>(
    `payments/acquirers/${acquirerUuid}`,
    { json: data }
  );
  return response.json();
}

export function useCreateAcquirerConfiguration(acquirerUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (config: AcquirerConfiguration) => {
      const current =
        queryClient.getQueryData<Acquirer>(
          queryKeys.acquirer(appUuid, acquirerUuid)
        ) ?? (await getAcquirer(appUuid, acquirerUuid));
      const configurations = [...current.configurations, config];
      return patchAcquirer(appUuid, acquirerUuid, { configurations });
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.acquirer(appUuid, acquirerUuid), data);
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}

export function useUpdateAcquirerConfiguration(
  acquirerUuid: string,
  network: AcquirerNetwork
) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (
      data: Partial<Omit<AcquirerConfiguration, 'network'>>
    ) => {
      const current =
        queryClient.getQueryData<Acquirer>(
          queryKeys.acquirer(appUuid, acquirerUuid)
        ) ?? (await getAcquirer(appUuid, acquirerUuid));
      const configurations = current.configurations.map((c) =>
        c.network === network ? { ...c, ...data } : c
      );
      return patchAcquirer(appUuid, acquirerUuid, { configurations });
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.acquirer(appUuid, acquirerUuid), data);
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}

export function useDeleteAcquirerConfiguration(
  acquirerUuid: string,
  network: AcquirerNetwork
) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async () => {
      const current =
        queryClient.getQueryData<Acquirer>(
          queryKeys.acquirer(appUuid, acquirerUuid)
        ) ?? (await getAcquirer(appUuid, acquirerUuid));
      const configurations = current.configurations.filter(
        (c) => c.network !== network
      );
      return patchAcquirer(appUuid, acquirerUuid, { configurations });
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.acquirer(appUuid, acquirerUuid), data);
      void queryClient.invalidateQueries(
        queryFilters.paginatedAcquirers(appUuid)
      );
    },
  });
}
